import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Heart,
  MapPin,
  Maximize2,
  BedDouble,
  Bath,
  Building2,
  Zap,
  ExternalLink,
  Share2,
  ChevronLeft,
  ChevronRight,
  ImageOff,
  AlertCircle,
  RefreshCw,
  ArrowUpSquare,
  Sofa,
  Home,
  Flame,
  Check,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { toast } from '../components/ui/Toast';
import { api, type Listing, type ListingImage, ApiError } from '../services/api';
import { useAuthStore } from '../stores/auth';
import { useListingsStore } from '../stores/listings';
import {
  formatPrice,
  formatSurface,
  formatDate,
  getPlatformLabel,
  getPropertyTypeLabel,
  getContractTypeLabel,
  cn,
} from '../lib/utils';

export default function ListingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { getListingById } = useListingsStore();
  const [listing, setListing] = useState<Listing | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isFavoriteLoading, setIsFavoriteLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());

  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const galleryRef = useRef<HTMLDivElement>(null);

  const fetchListing = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    if (id.startsWith('immo-') || id.startsWith('idealista-')) {
      const cachedListing = getListingById(id);
      if (cachedListing) {
        setListing(cachedListing);
        setIsFavorite(cachedListing.isFavorite || false);
        setIsLoading(false);
        return;
      }
    }

    try {
      const data = await api.getListing(id);
      setListing(data);
      setIsFavorite(data.isFavorite || false);
    } catch (err) {
      let errorMessage = 'Impossibile caricare i dettagli dell\'annuncio.';

      if (err instanceof ApiError) {
        if (err.status === 404) {
          errorMessage = 'Annuncio non trovato. Potrebbe essere stato rimosso.';
        } else if (err.status === 503 && id.startsWith('immo-')) {
          errorMessage = 'I dettagli completi non sono disponibili. Clicca "Vedi su Immobiliare.it" per visualizzare l\'annuncio.';
        } else {
          errorMessage = err.message;
        }
      }

      if (id.startsWith('immo-')) {
        const externalId = id.replace('immo-', '');
        setListing({
          id,
          externalId,
          platform: 'IMMOBILIARE',
          title: 'Annuncio Immobiliare.it',
          city: '',
          contractType: 'RENT',
          propertyType: 'APARTMENT',
          images: [],
          sourceUrl: `https://www.immobiliare.it/annunci/${externalId}/`,
          createdAt: new Date().toISOString(),
        });
        setError(errorMessage);
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [id, getListingById]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  const handleFavoriteToggle = async () => {
    if (!isAuthenticated) {
      toast({ title: 'Accedi per salvare i preferiti', variant: 'warning' });
      return;
    }
    if (!listing) return;

    setIsFavoriteLoading(true);
    try {
      if (isFavorite) {
        await api.removeFavorite(listing.id);
        setIsFavorite(false);
        toast({ title: 'Rimosso dai preferiti', variant: 'default' });
      } else {
        await api.addFavorite(listing.id);
        setIsFavorite(true);
        toast({ title: 'Aggiunto ai preferiti', variant: 'success' });
      }
    } catch {
      toast({ title: 'Impossibile completare l\'operazione', variant: 'error' });
    } finally {
      setIsFavoriteLoading(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share && listing) {
      try {
        await navigator.share({
          title: listing.title,
          text: `${listing.title} - ${listing.price ? formatPrice(listing.price, listing.contractType) : 'Prezzo su richiesta'}`,
          url: window.location.href,
        });
      } catch {
        // cancelled
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({ title: 'Link copiato negli appunti', variant: 'success' });
    }
  };

  const handleImageError = useCallback((index: number) => {
    setImageErrors((prev) => new Set(prev).add(index));
  }, []);

  const getImageUrl = useCallback((image: string | ListingImage): string => {
    return typeof image === 'string' ? image : image.url;
  }, []);

  const getImageLabel = useCallback((image: string | ListingImage): string | null => {
    return typeof image === 'string' ? null : image.label || null;
  }, []);

  const getValidImages = useCallback(() => {
    if (!listing?.images) return [];
    return listing.images.filter((_, index) => !imageErrors.has(index));
  }, [listing?.images, imageErrors]);

  const nextImage = () => {
    const validImages = getValidImages();
    if (validImages.length > 0) {
      setCurrentImageIndex((prev) => (prev === validImages.length - 1 ? 0 : prev + 1));
    }
  };

  const prevImage = () => {
    const validImages = getValidImages();
    if (validImages.length > 0) {
      setCurrentImageIndex((prev) => (prev === 0 ? validImages.length - 1 : prev - 1));
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchMove = (e: React.TouchEvent) => { touchEndX.current = e.touches[0].clientX; };
  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) { diff > 0 ? nextImage() : prevImage(); }
  };

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b px-4 py-3">
          <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="aspect-[4/3] md:aspect-[16/10] bg-gray-200 animate-pulse" />
        <div className="px-4 py-4 space-y-3">
          <div className="h-6 bg-gray-200 rounded animate-pulse w-3/4" />
          <div className="h-5 bg-gray-200 rounded animate-pulse w-1/3" />
          <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2" />
          <div className="grid grid-cols-3 gap-3 pt-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error (no listing)
  if (!listing) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <div className="flex justify-center mb-4">
          <div className="rounded-full bg-red-100 p-4">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">
          {error?.includes('non trovato') ? 'Annuncio non trovato' : 'Errore di caricamento'}
        </h2>
        <p className="text-gray-500 mb-6 max-w-md mx-auto">{error || 'L\'annuncio richiesto non esiste.'}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {!error?.includes('non trovato') && (
            <Button onClick={fetchListing}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Riprova
            </Button>
          )}
          <Button variant="outline" asChild>
            <Link to="/listings">Torna agli annunci</Link>
          </Button>
        </div>
      </div>
    );
  }

  const validImages = getValidImages();
  const currentImage = validImages[currentImageIndex];
  const currentImageUrl = currentImage ? getImageUrl(currentImage) : null;
  const currentImageLabel = currentImage ? getImageLabel(currentImage) : null;

  const keyInfo = [
    listing.surface && { icon: Maximize2, label: formatSurface(listing.surface) },
    listing.rooms && { icon: BedDouble, label: `${listing.rooms} ${listing.rooms === 1 ? 'locale' : 'locali'}` },
    listing.bathrooms && { icon: Bath, label: `${listing.bathrooms} bagn${listing.bathrooms > 1 ? 'i' : 'o'}` },
    listing.floor && { icon: Building2, label: `Piano ${listing.floor}` },
  ].filter(Boolean) as { icon: typeof Maximize2; label: string }[];

  return (
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-6">
      {/* Header mobile */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-100 lg:hidden">
        <div className="px-4 py-2 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 min-h-[44px] -ml-2 pl-2"
          >
            <ArrowLeft className="mr-1 h-5 w-5" />
            <span className="text-sm font-medium">Indietro</span>
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={handleShare}
              className="p-2.5 rounded-full hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Condividi"
            >
              <Share2 className="h-5 w-5 text-gray-600" />
            </button>
            <button
              onClick={handleFavoriteToggle}
              disabled={isFavoriteLoading}
              className={cn(
                "p-2.5 rounded-full min-h-[44px] min-w-[44px] flex items-center justify-center",
                isFavorite ? "bg-red-50" : "hover:bg-gray-100"
              )}
              aria-label={isFavorite ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
            >
              <Heart className={cn('h-5 w-5', isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600')} />
            </button>
          </div>
        </div>
      </div>

      {/* Desktop back */}
      <div className="hidden lg:block container mx-auto px-4 pt-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4 py-2 px-3 -ml-3 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Torna agli annunci
        </button>
      </div>

      {/* Info banner when data is incomplete */}
      {error && listing && (
        <div className="mx-4 lg:container lg:mx-auto mb-3 rounded-xl bg-amber-50 border border-amber-200 p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-amber-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Desktop: 2 column layout */}
      <div className="lg:container lg:mx-auto lg:px-4">
        <div className="lg:grid lg:grid-cols-3 lg:gap-6">

          {/* Left column (images + details) */}
          <div className="lg:col-span-2">
            {/* Image gallery - edge-to-edge on mobile, rounded on desktop */}
            <div
              ref={galleryRef}
              className="relative aspect-[4/3] md:aspect-[16/10] bg-gray-100 lg:rounded-2xl lg:overflow-hidden lg:shadow-sm"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {validImages.length > 0 && currentImageUrl ? (
                <>
                  <img
                    src={currentImageUrl}
                    alt={currentImageLabel || `${listing.title} - Immagine ${currentImageIndex + 1}`}
                    className="h-full w-full object-cover"
                    onError={() => handleImageError(currentImageIndex)}
                  />
                  {currentImageLabel && (
                    <div className="absolute top-3 left-3 rounded-lg bg-black/70 px-2.5 py-1 text-xs text-white">
                      {currentImageLabel}
                    </div>
                  )}

                  {validImages.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 shadow-md flex items-center justify-center active:scale-95 transition-all"
                        aria-label="Immagine precedente"
                      >
                        <ChevronLeft className="h-5 w-5 text-gray-700" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 shadow-md flex items-center justify-center active:scale-95 transition-all"
                        aria-label="Immagine successiva"
                      >
                        <ChevronRight className="h-5 w-5 text-gray-700" />
                      </button>

                      {/* Dots mobile */}
                      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                        {validImages.slice(0, 7).map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={cn(
                              "h-1.5 rounded-full transition-all",
                              currentImageIndex === index ? "w-4 bg-white" : "w-1.5 bg-white/50"
                            )}
                            aria-label={`Immagine ${index + 1}`}
                          />
                        ))}
                        {validImages.length > 7 && (
                          <span className="text-white/70 text-[10px] ml-0.5 self-center">+{validImages.length - 7}</span>
                        )}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="flex h-full flex-col items-center justify-center text-gray-400">
                  <ImageOff className="h-10 w-10 mb-2" />
                  <span className="text-sm">Nessuna immagine</span>
                </div>
              )}
            </div>

            {/* Desktop thumbnails */}
            {validImages.length > 1 && (
              <div className="hidden lg:flex gap-2 mt-3 overflow-x-auto pb-1">
                {validImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={cn(
                      'flex-shrink-0 h-16 w-20 rounded-lg overflow-hidden border-2 transition-all',
                      currentImageIndex === index
                        ? 'border-blue-500 ring-2 ring-blue-200'
                        : 'border-transparent hover:border-gray-300'
                    )}
                  >
                    <img
                      src={getImageUrl(image)}
                      alt={`Thumbnail ${index + 1}`}
                      className="h-full w-full object-cover"
                      onError={() => handleImageError(index)}
                    />
                  </button>
                ))}
              </div>
            )}

            {/* ====== MOBILE: Info section (below images) ====== */}
            <div className="lg:hidden px-4 pt-4 pb-2 bg-white">
              {/* Badges */}
              <div className="flex items-center gap-2 mb-2">
                <span className={cn(
                  'rounded-lg px-2.5 py-1 text-xs font-semibold',
                  listing.contractType === 'SALE' ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'
                )}>
                  {getContractTypeLabel(listing.contractType)}
                </span>
                <span className="rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                  {getPropertyTypeLabel(listing.propertyType)}
                </span>
                <span className="rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
                  {getPlatformLabel(listing.platform)}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-lg font-bold text-gray-900 leading-tight mb-1">
                {listing.title}
              </h1>

              {/* Location */}
              <div className="flex items-center text-gray-500 text-sm mb-3">
                <MapPin className="mr-1 h-4 w-4 flex-shrink-0" />
                <span className="truncate">
                  {listing.address || listing.city}
                  {listing.province && `, ${listing.province}`}
                </span>
              </div>

              {/* Price */}
              {listing.price ? (
                <p className="text-2xl font-bold text-gray-900 mb-1">
                  {formatPrice(listing.price, listing.contractType)}
                </p>
              ) : (
                <p className="text-lg font-medium text-gray-500 mb-1">Prezzo su richiesta</p>
              )}

              {/* Agency */}
              {listing.agencyName && (
                <p className="text-sm text-gray-500 mb-1">{listing.agencyName}</p>
              )}

              {/* Date */}
              <p className="text-xs text-gray-400">
                Pubblicato {formatDate(listing.createdAt)}
              </p>
            </div>

            {/* Key info pills - mobile */}
            {keyInfo.length > 0 && (
              <div className="lg:hidden flex gap-2 overflow-x-auto px-4 py-3 bg-white border-b border-gray-100">
                {keyInfo.map((info, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1.5 bg-gray-50 rounded-full px-3 py-2 flex-shrink-0"
                  >
                    <info.icon className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium text-gray-900 whitespace-nowrap">{info.label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Cards section */}
            <div className="px-4 lg:px-0 pt-4 space-y-4">
              {/* Description */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardContent className="p-4 md:p-6">
                  <h2 className="text-base font-semibold text-gray-900 mb-3">Descrizione</h2>
                  {listing.description ? (
                    <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">
                      {listing.description}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">Nessuna descrizione disponibile</p>
                  )}
                </CardContent>
              </Card>

              {/* Features */}
              <Card className="rounded-2xl border-0 shadow-sm">
                <CardContent className="p-4 md:p-6">
                  <h2 className="text-base font-semibold text-gray-900 mb-3">Caratteristiche</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {listing.surface && (
                      <FeatureItem icon={Maximize2} label="Superficie" value={formatSurface(listing.surface)} color="blue" />
                    )}
                    {listing.rooms && (
                      <FeatureItem icon={BedDouble} label="Locali" value={listing.rooms.toString()} color="blue" />
                    )}
                    {listing.bathrooms && (
                      <FeatureItem icon={Bath} label="Bagni" value={listing.bathrooms.toString()} color="blue" />
                    )}
                    {listing.floor && (
                      <FeatureItem icon={Building2} label="Piano" value={listing.floor} color="blue" />
                    )}
                    {listing.hasLift && (
                      <FeatureItem icon={ArrowUpSquare} label="Ascensore" value="Si" color="green" />
                    )}
                    {listing.isFurnished && (
                      <FeatureItem icon={Sofa} label="Arredato" value="Si" color="purple" />
                    )}
                    {listing.hasBalcony && (
                      <FeatureItem icon={Home} label="Balcone" value="Si" color="teal" />
                    )}
                    {listing.heating && (
                      <FeatureItem icon={Flame} label="Riscaldamento" value={listing.heating} color="orange" />
                    )}
                    {listing.propertyStatus && (
                      <FeatureItem icon={Check} label="Stato" value={listing.propertyStatus} color="indigo" />
                    )}
                    {listing.energyClass && (
                      <FeatureItem icon={Zap} label="Classe energ." value={listing.energyClass} color="yellow" />
                    )}
                  </div>
                  {!listing.surface && !listing.rooms && !listing.bathrooms && !listing.floor && !listing.energyClass && (
                    <p className="text-sm text-gray-400 italic">Nessuna caratteristica disponibile</p>
                  )}
                </CardContent>
              </Card>

              {/* Additional Features */}
              {listing.features && listing.features.length > 0 && (
                <Card className="rounded-2xl border-0 shadow-sm">
                  <CardContent className="p-4 md:p-6">
                    <h2 className="text-base font-semibold text-gray-900 mb-3">Servizi e dotazioni</h2>
                    <div className="flex flex-wrap gap-2">
                      {listing.features.map((feature, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1.5 text-xs text-gray-700"
                        >
                          <Check className="mr-1 h-3 w-3 text-green-500" />
                          {feature}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* ====== DESKTOP: Sidebar ====== */}
          <div className="hidden lg:block">
            <Card className="sticky top-24 rounded-2xl border-0 shadow-sm">
              <CardContent className="p-6">
                {/* Badges */}
                <div className="flex gap-2 mb-4">
                  <span className={cn(
                    'rounded-lg px-3 py-1 text-sm font-semibold',
                    listing.contractType === 'SALE' ? 'bg-blue-500 text-white' : 'bg-green-500 text-white'
                  )}>
                    {getContractTypeLabel(listing.contractType)}
                  </span>
                  <span className="rounded-lg bg-gray-100 px-3 py-1 text-sm font-medium text-gray-600">
                    {getPropertyTypeLabel(listing.propertyType)}
                  </span>
                </div>

                {/* Title */}
                <h1 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                  {listing.title}
                </h1>

                {/* Location */}
                <div className="flex items-start text-gray-500 mb-4">
                  <MapPin className="mr-2 h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-gray-700">
                      {listing.address || listing.city}
                      {listing.province && `, ${listing.province}`}
                    </span>
                    {(listing.zone || listing.microzone) && (
                      <p className="text-sm text-gray-400 mt-0.5">
                        {listing.zone}
                        {listing.microzone && listing.zone && ' - '}
                        {listing.microzone}
                      </p>
                    )}
                  </div>
                </div>

                {/* Price */}
                <div className="py-4 border-t border-b border-gray-100 mb-4">
                  {listing.price ? (
                    <p className="text-3xl font-bold text-gray-900">
                      {formatPrice(listing.price, listing.contractType)}
                    </p>
                  ) : (
                    <p className="text-xl font-medium text-gray-500">Prezzo su richiesta</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 mb-4">
                  <Button
                    variant={isFavorite ? 'default' : 'outline'}
                    className="flex-1 h-12"
                    onClick={handleFavoriteToggle}
                    disabled={isFavoriteLoading}
                  >
                    <Heart className={cn('mr-2 h-5 w-5', isFavorite && 'fill-current')} />
                    {isFavoriteLoading ? 'Salvataggio...' : isFavorite ? 'Salvato' : 'Salva'}
                  </Button>
                  <Button variant="outline" size="icon" onClick={handleShare} className="h-12 w-12" aria-label="Condividi">
                    <Share2 className="h-5 w-5" />
                  </Button>
                </div>

                {/* External link */}
                <a href={listing.sourceUrl} target="_blank" rel="noopener noreferrer" className="block">
                  <Button variant="outline" className="w-full h-12">
                    <ExternalLink className="mr-2 h-5 w-5" />
                    Vedi su {getPlatformLabel(listing.platform)}
                  </Button>
                </a>

                {/* Meta */}
                <div className="mt-6 pt-4 border-t border-gray-100 text-sm text-gray-500 space-y-1">
                  {listing.agencyName && <p className="font-medium text-gray-700">{listing.agencyName}</p>}
                  <p>Fonte: {getPlatformLabel(listing.platform)}</p>
                  <p>Pubblicato: {formatDate(listing.createdAt)}</p>
                  {listing.updatedAt && listing.updatedAt !== listing.createdAt && (
                    <p>Aggiornato: {formatDate(listing.updatedAt)}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Mobile bottom bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 safe-bottom" style={{ zIndex: 1050 }}>
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            {listing.price ? (
              <p className="text-lg font-bold text-gray-900 truncate">
                {formatPrice(listing.price, listing.contractType)}
              </p>
            ) : (
              <p className="text-sm font-medium text-gray-500">Prezzo su richiesta</p>
            )}
          </div>
          <a href={listing.sourceUrl} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
            <Button className="h-11 px-5 text-sm font-semibold">
              <ExternalLink className="mr-1.5 h-4 w-4" />
              Vedi annuncio
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}

function FeatureItem({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: typeof Maximize2;
  label: string;
  value: string;
  color: 'blue' | 'green' | 'purple' | 'teal' | 'orange' | 'indigo' | 'yellow';
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-500',
    green: 'bg-green-50 text-green-500',
    purple: 'bg-purple-50 text-purple-500',
    teal: 'bg-teal-50 text-teal-500',
    orange: 'bg-orange-50 text-orange-500',
    indigo: 'bg-indigo-50 text-indigo-500',
    yellow: 'bg-yellow-50 text-yellow-600',
  };

  return (
    <div className="flex items-center gap-2.5">
      <div className={cn('flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0', colorClasses[color])}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
        <p className="text-sm font-medium text-gray-900 truncate">{value}</p>
      </div>
    </div>
  );
}
